'use client';

import { useEffect, useState } from 'react';
import HeroSection from './HeroSection';
import FoundingStorySection from './FoundingStorySection';
import VisionMissionSection from './VisionMissionSection';
import LeadershipTeamSection from './LeadershipTeamSection';
import CompanyCultureSection from './CompanyCultureSection';
import CertificationsSection from './CertificationsSection';

interface Milestone {
  year: string;
  title: string;
  description: string;
  icon: string;
}

interface VisionMissionItem {
  title: string;
  content: string;
  icon: string;
  color: string;
}

interface TeamMember {
  name: string;
  position: string;
  expertise: string;
  bio: string;
  image: string;
  imageAlt: string;
  credentials: string[];
  email: string;
  phone: string;
}

interface CultureValue {
  title: string;
  description: string;
  icon: string;
}

interface Certification {
  title: string;
  number: string;
  issuer: string;
  issueDate: string;
  expiryDate: string;
  status: string;
  description: string;
  icon: string;
}

interface AboutInteractiveProps {
  hero: { title: string; subtitle: string; description: string; heroImage: string; heroImageAlt: string };
  foundingStory: string;
  milestones: Milestone[];
  visionMissionItems: VisionMissionItem[];
  cultureValues: CultureValue[];
  cultureTags: string[];
  communityImage: string;
  communityImageAlt: string;
  communityImpact: string;
  leadershipTitle?: string;
  leadershipSubtitle?: string;
  teamMembers: TeamMember[];
  certificationsTitle?: string;
  certificationsSubtitle?: string;
  trustBadges?: { title: string; description: string; icon: string }[];
  certifications: Certification[];
}

const AboutInteractive = ({
  hero,
  foundingStory,
  milestones,
  visionMissionItems,
  cultureValues,
  cultureTags,
  communityImage,
  communityImageAlt,
  communityImpact,
  leadershipTitle,
  leadershipSubtitle,
  teamMembers,
  certificationsTitle,
  certificationsSubtitle,
  trustBadges,
  certifications,
}: AboutInteractiveProps) => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <div data-component-id="app/about/components/AboutInteractive.tsx:99:6" data-component-path="app/about/components/AboutInteractive.tsx" data-component-line="99" data-component-end-line="107" data-component-file="AboutInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
        <div data-component-id="app/about/components/AboutInteractive.tsx:100:8" data-component-path="app/about/components/AboutInteractive.tsx" data-component-line="100" data-component-end-line="106" data-component-file="AboutInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22container%20mx-auto%20px-4%20py-20%22%7D" className="container mx-auto px-4 py-20">
          <div data-component-id="app/about/components/AboutInteractive.tsx:101:10" data-component-path="app/about/components/AboutInteractive.tsx" data-component-line="101" data-component-end-line="105" data-component-file="AboutInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%20space-y-8%22%7D" className="animate-pulse space-y-8">
            <div data-component-id="app/about/components/AboutInteractive.tsx:102:12" data-component-path="app/about/components/AboutInteractive.tsx" data-component-line="102" data-component-end-line="102" data-component-file="AboutInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-96%20bg-muted%20rounded-lg%22%7D" className="h-96 bg-muted rounded-lg"></div>
            <div data-component-id="app/about/components/AboutInteractive.tsx:103:12" data-component-path="app/about/components/AboutInteractive.tsx" data-component-line="103" data-component-end-line="103" data-component-file="AboutInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-64%20bg-muted%20rounded-lg%22%7D" className="h-64 bg-muted rounded-lg"></div>
            <div data-component-id="app/about/components/AboutInteractive.tsx:104:12" data-component-path="app/about/components/AboutInteractive.tsx" data-component-line="104" data-component-end-line="104" data-component-file="AboutInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-64%20bg-muted%20rounded-lg%22%7D" className="h-64 bg-muted rounded-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-component-id="app/about/components/AboutInteractive.tsx:112:4" data-component-path="app/about/components/AboutInteractive.tsx" data-component-line="112" data-component-end-line="130" data-component-file="AboutInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
      <HeroSection data-component-id="app/about/components/AboutInteractive.tsx:113:6" data-component-path="app/about/components/AboutInteractive.tsx" data-component-line="113" data-component-end-line="113" data-component-file="AboutInteractive.tsx" data-component-name="HeroSection" data-component-content="%7B%22elementName%22%3A%22HeroSection%22%2C%22%5Bspread%5D%22%3A%22true%22%7D" {...hero} />
      <FoundingStorySection data-component-id="app/about/components/AboutInteractive.tsx:114:6" data-component-path="app/about/components/AboutInteractive.tsx" data-component-line="114" data-component-end-line="114" data-component-file="AboutInteractive.tsx" data-component-name="FoundingStorySection" data-component-content="%7B%22elementName%22%3A%22FoundingStorySection%22%7D" story={foundingStory} milestones={milestones} />
      <VisionMissionSection data-component-id="app/about/components/AboutInteractive.tsx:115:6" data-component-path="app/about/components/AboutInteractive.tsx" data-component-line="115" data-component-end-line="115" data-component-file="AboutInteractive.tsx" data-component-name="VisionMissionSection" data-component-content="%7B%22elementName%22%3A%22VisionMissionSection%22%7D" items={visionMissionItems} />
      <CompanyCultureSection data-component-id="app/about/components/AboutInteractive.tsx:116:6" data-component-path="app/about/components/AboutInteractive.tsx" data-component-line="116" data-component-end-line="122" data-component-file="AboutInteractive.tsx" data-component-name="CompanyCultureSection" data-component-content="%7B%22elementName%22%3A%22CompanyCultureSection%22%7D"
        values={cultureValues}
        communityImage={communityImage}
        communityImageAlt={communityImageAlt}
        communityImpact={communityImpact}
        tags={cultureTags}
      />
      <LeadershipTeamSection data-component-id="app/about/components/AboutInteractive.tsx:123:6" data-component-path="app/about/components/AboutInteractive.tsx" data-component-line="123" data-component-end-line="123" data-component-file="AboutInteractive.tsx" data-component-name="LeadershipTeamSection" data-component-content="%7B%22elementName%22%3A%22LeadershipTeamSection%22%7D" teamMembers={teamMembers} title={leadershipTitle} subtitle={leadershipSubtitle} />
      <CertificationsSection data-component-id="app/about/components/AboutInteractive.tsx:124:6" data-component-path="app/about/components/AboutInteractive.tsx" data-component-line="124" data-component-end-line="129" data-component-file="AboutInteractive.tsx" data-component-name="CertificationsSection" data-component-content="%7B%22elementName%22%3A%22CertificationsSection%22%7D"
        certifications={certifications}
        title={certificationsTitle}
        subtitle={certificationsSubtitle}
        trustBadges={trustBadges}
      />
    </div>
  );
};

export default AboutInteractive;
